/**
 * Test if an img src resource is loaded.
 * Note that `complete` can be true even when an error occurs (ie. 404). For this reason we also need to check for the `natural` dimensions. These are only present when the image resource exists.
 * @param imgElement {Element} The `img` element that we're checking to see if the src resource is loaded.
 * @returns {boolean}
 */
export function isImgLoaded(imgElement) {
    if (!imgElement) return;
    if (imgElement.complete) {
        // Checking the `naturalWidth` and `naturalHeight` properties is the *only* why to check if the img src has actually loaded.
        return !(!imgElement.naturalWidth || !imgElement.naturalHeight);
    } else {
        return false;
    }
}

/**
 * Loads an image URL in a detached `Image` and resolves only once it has actually decoded.
 *
 * The point is to keep a URL that might fail away from any `img` element the user can see. An
 * `img` whose `src` fails paints the renderer's broken-image glyph (Chromium) or drops what it was
 * showing — so a placeholder must never be overwritten with a URL until that URL is known good.
 * Assign to the visible element in the `then`, and there is nothing to undo in the `catch`.
 *
 * Always rejects rather than hanging: on the image's own error, and on a timeout, because a
 * request that never settles would otherwise leave a caller waiting forever.
 *
 * @param url {string} The image URL to load.
 * @param options {Object} `timeout` in ms (default 4000), matching the carousel's ImageLoader.
 * @returns {Promise<HTMLImageElement>} Resolves with the loaded (detached) image.
 */
export function preloadImage(url, options = {}) {
    const timeout = options.timeout === undefined ? 4000 : options.timeout;

    return new Promise((resolve, reject) => {
        if (!url) return reject(new Error('preloadImage: no url given'));

        const image = new Image();
        let timeoutId = null;

        const settle = (callback) => {
            if (timeoutId) clearTimeout(timeoutId);
            image.onload = null;
            image.onerror = null;
            callback();
        };

        image.onload = () => settle(() => {
            // `complete` alone lies (it is true for a 404 too), so isImgLoaded checks the natural
            // dimensions. A decoded-but-empty image is a failure, not a success.
            isImgLoaded(image) ? resolve(image) : reject(new Error(`preloadImage: empty image ${url}`));
        });

        image.onerror = () => settle(() => reject(new Error(`preloadImage: failed to load ${url}`)));

        if (timeout) {
            timeoutId = setTimeout(() => settle(() => reject(new Error(`preloadImage: timed out after ${timeout}ms ${url}`))), timeout);
        }

        image.src = url;
    });
}

/**
 * Encodes an SVG string to be safely used as a CSS background image URL.
 *
 * This function applies URI encoding to ensure all characters in the SVG string
 * are correctly represented in a URL context. It specifically handles special
 * characters like quotes to prevent syntax issues in CSS. The resulting string
 * is returned as a formatted data URI suitable for CSS background-image usage.
 *
 * @param {string} svgString - The raw SVG markup to be encoded.
 * @return {string} The encoded SVG string wrapped in a CSS URL function.
 */
export function svgForBackgroundImage(svgString) {
    // Encode the SVG string using encodeURIComponent
    const encodedSvg = encodeURIComponent(svgString)
        .replace(/'/g, "%27")  // Encode single quotes
        .replace(/"/g, "%22"); // Encode double quotes

    // Return the fully encoded SVG ready for use as a background image
    return `url('data:image/svg+xml,${encodedSvg}')`;
}
